import type { RagProfileConfig, StaticProfileConfig } from '@lokalise/translation-api-schemas'
import { useCallback, useMemo } from 'react'
import {
  isPredefinedProfile,
  isStaticProfile,
  isUserCreatedProfile,
  type UserCreatedProfile,
} from '../types/tag.ts'
import {
  type AiPermissionDetails,
  type AiPermissions,
  useFetchAiPermissions,
} from './useFetchAiPermissions.ts'

type AnyProfile = StaticProfileConfig | RagProfileConfig | UserCreatedProfile

type AiProfilePermissionUtilities = {
  isLoadingPermissions: boolean
  isPermissionsLoadSuccess: boolean
  permissions: AiPermissions
  canView: (profile: AnyProfile | undefined) => boolean
  canEdit: (profile: AnyProfile | undefined) => boolean
}

export const useAiProfilePermissionUtilities = (): AiProfilePermissionUtilities => {
  const { permissions: maybePermissions, isPending, isSuccess } = useFetchAiPermissions()

  const permissions = useMemo(
    (): AiPermissions => ({
      staticProfile: resolveDetails(maybePermissions?.staticProfile),
      predefinedProfile: resolveDetails(maybePermissions?.predefinedProfile),
      customProfile: resolveDetails(maybePermissions?.customProfile),
    }),
    [maybePermissions],
  )

  const canView = useCallback(
    (profile: AnyProfile | undefined) =>
      !isPending && resolvePermissionForProfile(profile, permissions, ({ canView }) => canView),
    [isPending, permissions],
  )

  const canEdit = useCallback(
    (profile: AnyProfile | undefined) =>
      !isPending && resolvePermissionForProfile(profile, permissions, ({ canEdit }) => canEdit),
    [isPending, permissions],
  )

  return {
    canView,
    canEdit,
    permissions,
    isLoadingPermissions: isPending,
    isPermissionsLoadSuccess: isSuccess,
  }
}

const resolvePermissionForProfile = (
  profile: AnyProfile | undefined,
  permissions: AiPermissions,
  resolve: (details: AiPermissionDetails) => boolean,
) => {
  if (profile === undefined) return false

  if (isStaticProfile(profile)) {
    return resolve(permissions.staticProfile)
  }

  if (isPredefinedProfile(profile)) {
    return resolve(permissions.predefinedProfile)
  }

  if (isUserCreatedProfile(profile)) {
    return resolve(permissions.customProfile)
  }

  return false
}

const resolveDetails = (permission: AiPermissionDetails | undefined): AiPermissionDetails => ({
  canView: permission?.canView ?? false,
  canEdit: (permission?.canView ?? false) && (permission?.canEdit ?? false),
})
